/**
 * useUpdateDocumentDatabase Hook
 *
 * Custom React Query mutation hook for updating document databases.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentDatabaseService } from '../../../api/document-database.service';
import type { DocumentDatabaseUpdateDTO } from '../../../types/document-database.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface UseUpdateDocumentDatabaseOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

/**
 * Hook for updating document database
 * @param tenantId - Tenant ID
 * @param documentDBId - DocumentDB ID
 * @param options - Success/error callbacks
 */
export const useUpdateDocumentDatabase = (
  tenantId: string,
  documentDBId: string,
  options?: UseUpdateDocumentDatabaseOptions
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DocumentDatabaseUpdateDTO) =>
      documentDatabaseService.updateDocumentDatabase(tenantId, documentDBId, data),
    onSuccess: () => {
      // Invalidate both list and details queries
      queryClient.invalidateQueries({ queryKey: ['documentDatabases', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['documentDatabases', tenantId, documentDBId] });
      options?.onSuccess?.();
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      options?.onError?.(errorMessage);
    },
  });
};
